import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    Box,
    Card,
} from '@material-ui/core';
import {
    Loading,
    SaveButton,
    SaveContextProvider,
    useNotify,
    useGetIdentity,
    Toolbar,
    ToolbarProps,
    useAuthProvider,
    useAuthenticated,
} from 'react-admin';
import { mutateUserProfile } from "api/mutations/userProfile";
import { UserEditForm } from "reactAdmin/resource/user/userEditForm";
import { convertFileToBase64 } from "reactAdmin/resource/user/convertFileToBase64";
import { useDispatch } from "react-redux";
import { profile } from "store/auth/actions";

const ProfileContext = createContext({
    profileVersion: 0,
    refreshProfile: async () => {}
});

export const ProfileProvider = ({ children } : {children: JSX.Element}) => {
    const [profileVersion, setProfileVersion] = useState(0);
    const dispatch = useDispatch();
    const authProvider = useAuthProvider();

    const context = useMemo(
        () => ({
            profileVersion,
            refreshProfile: async () => {
                dispatch(profile(undefined));
                if (authProvider.getIdentity) {
                    await authProvider.getIdentity();
                }
                setProfileVersion((currentVersion) => currentVersion + 1);
            }
        }),
        [authProvider, dispatch, profileVersion]
    );

    return (
        <ProfileContext.Provider value={context}>
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => useContext(ProfileContext);

const ProfileEditToolbar = (props: ToolbarProps) => {
    const {
        alwaysEnableSaveButton,
        handleSubmitWithRedirect,
        handleSubmit,
        invalid,
        pristine,
        redirect,
        saving,
        submitOnEnter,
        validating,
    } = props;

    const disabled = alwaysEnableSaveButton === true || pristine || validating;

    return (
        <Toolbar {...props} >
            <SaveButton
                handleSubmitWithRedirect={
                    handleSubmitWithRedirect || handleSubmit
                }
                disabled={disabled}
                invalid={invalid}
                redirect={redirect}
                saving={saving || validating}
                submitOnEnter={submitOnEnter}
            />
        </Toolbar>
    );
}

export const ProfileEdit = () => {
    useAuthenticated();
    const notify = useNotify();
    const [saving, setSaving] = useState(false);
    const { refreshProfile } = useProfile();

    const { loaded, identity } = useGetIdentity();

    const handleSave = useCallback(
        async (values) => {
            if (values.avatar && values.avatar.rawFile instanceof File) {
                values.avatar = await convertFileToBase64(values.avatar.rawFile);
            } else if (values.avatar !== null) {
                delete values.avatar;
            }

            try {
                setSaving(true);
                await mutateUserProfile(values)
                setSaving(false);
                refreshProfile();
                notify("Your profile has been updated", {type: "info"});
            } catch (error) {
                setSaving(false);
                notify("A technical error occured while updating your profile. Please try later.", {type: "warning"});
            }
        },
        [notify, refreshProfile]
    );

    const saveContext = useMemo(() => ({
        save: handleSave,
        saving
    }), [saving, handleSave]);

    if (!loaded) {
        return <Loading />;
    }

    return (
        <Box mt={2}>
            <SaveContextProvider value={saveContext}>
                <Card>
                    <UserEditForm
                        myProfile={true}
                        save={handleSave}
                        record={identity ? {
                            id: identity.id,
                            firstName: identity.firstName,
                            lastName: identity.lastName,
                            gender: identity.gender,
                            avatar: identity.avatar ? {src: identity.avatar} : identity.avatar,
                            dateOfBirth: identity.dateOfBirth,
                            countryId: identity.countryId,
                            emailAddress: identity.emailAddress,
                            phoneNumber: identity.phoneNumber,
                            organizationId: identity.organizationId,
                            languageId: identity.languageId,
                            timeZone: identity.timeZone,
                            role: identity.role,
                        } : {}}
                        toolbar={<ProfileEditToolbar />}
                    />
                </Card>
            </SaveContextProvider>
        </Box>
    );
};